import type {
  WinStreakTowerDto,
  WinStreakTowerRewardDto,
} from "../../../api/dto/win_streak_tower.dto.js";

export interface AdventureFloorInfo
{
  id: number;
  wins_required: number;
  rewards: WinStreakTowerRewardDto[];
  level: number;
  passed: boolean;
  reward_collected: boolean;
}

export enum AdventureBoxState
{
  Locked,
  Claimable,
  Open,
}

export interface AdventureBadgeInfo
{
  x: number;
  y: number;
  floor_info: AdventureFloorInfo;
}

export function buildAdventureFloors( tower: WinStreakTowerDto ): AdventureFloorInfo[]
{
  let cumulative_level = 0;

  return tower.floors.map( floor =>
  {
    cumulative_level += floor.wins_required;

    return {
      id: floor.id,
      wins_required: floor.wins_required,
      rewards: floor.rewards,
      level: cumulative_level,
      passed: floor.reached,
      reward_collected: floor.claimed,
    };
  } );
}

export function buildAdventureZeroFloor(): AdventureFloorInfo
{
  return {
    id: 0,
    wins_required: 0,
    rewards: [],
    level: 0,
    passed: true,
    reward_collected: false,
  };
}

export function hasUnclaimedAdventureReward( tower: WinStreakTowerDto | null | undefined ): boolean
{
  return tower?.floors.some( floor => floor.claimable ) === true;
}

export function countClaimableAdventureRewards( tower: WinStreakTowerDto | null | undefined ): number
{
  return tower?.floors.filter( floor => floor.claimable && !floor.claimed ).length ?? 0;
}

export interface WinStreakProgress
{
  wins_progress: number;
  wins_required: number;
}

/**
 * Wins collected on the floor the player is climbing, against the wins that
 * floor needs. Returns null when there is no floor left to reach. The counts
 * are per floor — buildAdventureFloors() is what turns them into the
 * cumulative levels shown on the tower itself.
 */
export function getWinStreakProgress(
  tower: WinStreakTowerDto | null | undefined
): WinStreakProgress | null
{
  if ( !tower || tower.completed )
    return null;

  const next_floor = tower.floors.find( floor => floor.id === tower.next_floor_id );
  if ( !next_floor || next_floor.wins_required <= 0 )
    return null;

  return {
    wins_progress: tower.wins_progress,
    wins_required: next_floor.wins_required,
  };
}
